/**
 * MySpineo — scroll-driven reveal for the 3-photo product sequence.
 *
 * Progressive enhancement:
 * - No JS, or IntersectionObserver unsupported, or prefers-reduced-motion:
 *   the three frames stay at their default CSS state (fully visible,
 *   no animation) — see sections/mspo-product-reveal.liquid stylesheet.
 * - Otherwise: each frame fades / translates / scales in (0.98 → 1) the
 *   first time it enters the viewport. No rotation, no levitation, no
 *   mechanical opening effect of any kind.
 *
 * Uses the theme's own scroll-container helper so this also works
 * correctly on desktop, where Horizon's "squeeze" layout scrolls
 * `.page-wrapper` rather than the document/window.
 */

let getIntersectionRoot;
let prefersReducedMotion;

try {
  ({ getIntersectionRoot } = await import('@theme/scroll-container'));
} catch (error) {
  getIntersectionRoot = null;
}

try {
  ({ prefersReducedMotion } = await import('@theme/utilities'));
} catch (error) {
  prefersReducedMotion = null;
}

class MspoProductReveal extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const reducedMotion =
      typeof prefersReducedMotion === 'function'
        ? prefersReducedMotion()
        : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const frames = this.querySelectorAll('[data-mspo-frame]');
    if (!frames.length) return;

    let root = null;
    try {
      root = typeof getIntersectionRoot === 'function' ? getIntersectionRoot() : null;
    } catch (error) {
      root = null;
    }

    this.classList.add('mspo-reveal--js');

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.#observer.unobserve(entry.target);
          }
        }
      },
      {
        root: root || null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.25,
      }
    );

    frames.forEach((frame) => this.#observer.observe(frame));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('mspo-product-reveal')) {
  customElements.define('mspo-product-reveal', MspoProductReveal);
}
